import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

export default function GPSEnableNotification() {
  const [showNotification, setShowNotification] = useState(false);
  const [gpsStatus, setGpsStatus] = useState("unknown");
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(userAgent));
    setIsAndroid(/Android/.test(userAgent));

    // Check GPS permission status
    checkGPSStatus();
  }, []);

  const checkGPSStatus = async () => {
    if (typeof navigator === "undefined") return;

    if (!navigator.geolocation) {
      setGpsStatus("unsupported");
      setShowNotification(true);
      return;
    }

    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permission = await navigator.permissions.query({ name: "geolocation" });
        setGpsStatus(permission.state);

        if (permission.state === "denied" || permission.state === "prompt") {
          setShowNotification(true);
        }

        permission.onchange = () => {
          setGpsStatus(permission.state);
          if (permission.state === "granted") {
            setShowNotification(false);
            toast.success("GPS enabled successfully!");
          } else if (permission.state === "denied") {
            setShowNotification(true);
          }
        };
      } catch (err) {
        // Fallback for browsers that don't support permissions API
        setGpsStatus("unknown");
        setShowNotification(true);
      }
    } else {
      // Permissions API not available
      setGpsStatus("unknown");
      setShowNotification(true);
    }
  };

  const requestGPSPermission = () => {
    if (!navigator.geolocation) {
      toast.error("GPS is not supported on this device");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Permission granted
        setShowNotification(false);
        toast.success("GPS enabled successfully!");
      },
      (error) => {
        // Permission denied or error
        console.log("GPS permission error:", error);
        toast.error("GPS permission denied. Please enable location services in your browser settings.");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const dismissNotification = () => {
    setShowNotification(false);
  };

  const getDeviceInstructions = () => {
    if (isIOS) {
      return {
        title: "Enable GPS on iPhone/iPad",
        steps: [
          "Go to Settings → Privacy & Security",
          "Tap Location Services",
          "Turn on Location Services",
          "Find Safari Websites and tap it",
          "Select 'Allow' or 'Ask' for location access"
        ]
      };
    } else if (isAndroid) {
      return {
        title: "Enable GPS on Android",
        steps: [
          "Open Chrome browser",
          "Tap the lock/info icon in address bar",
          "Tap Site settings",
          "Tap Location",
          "Select 'Allow'"
        ]
      };
    } else {
      return {
        title: "Enable GPS in Browser",
        steps: [
          "Click the location icon in your browser's address bar",
          "Select 'Allow' when prompted",
          "Or go to browser settings → Privacy → Location"
        ]
      };
    }
  };

  if (!showNotification) return null;

  const instructions = getDeviceInstructions();

  return (
    <div className="fixed top-20 left-4 right-4 z-[110] md:left-auto md:right-4 md:w-96">
      <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-amber-800 mb-1">
              GPS Access Required
            </h3>
            <p className="text-sm text-amber-700 mb-3">
              Location services are disabled. Enable GPS to use all features of this application.
            </p>

            <div className="space-y-2">
              <button
                onClick={requestGPSPermission}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Enable GPS
              </button>

              <details className="text-xs">
                <summary className="text-amber-700 hover:text-amber-800 cursor-pointer font-medium">
                  How to enable GPS {isIOS ? "on iOS" : isAndroid ? "on Android" : "in browser"}
                </summary>
                <div className="mt-2 p-2 bg-amber-100 rounded text-amber-800">
                  <h4 className="font-medium mb-1">{instructions.title}</h4>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    {instructions.steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              </details>
            </div>
          </div>
          <button
            onClick={dismissNotification}
            className="flex-shrink-0 text-amber-400 hover:text-amber-600 transition-colors"
            aria-label="Dismiss notification"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}